import { Heading } from 'react-aria-components';
import Button, { ButtonProps } from 'src/components/ui/Button';
import Dialog from 'src/components/ui/Dialog';
import BinIcon from 'src/assets/icons/bin-1.svg';
import Illustration from 'src/assets/illustrations/fingerprint-delete-cross.svg';

import type { ReactNode } from 'react';

interface ConfirmationDialogProps {
  title?: string;
  children: ReactNode;
  image?: string | null;
  buttonTitle?: string;
  cancelTitle?: string;
  buttonProps?: Omit<ButtonProps, 'children' | 'onPress' | 'loading'>;
  loading?: boolean;
  onConfirm: () => Promise<void>;
  onCancel?: () => void;
}

const ConfirmationDialog = ({
  title = 'Are you sure?',
  loading = false,
  buttonTitle = 'Confirm',
  cancelTitle = 'Cancel',
  buttonProps = {},
  image = Illustration,
  children,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps) => {
  return (
    <Dialog
      role="alertdialog"
      className="p-0 overflow-hidden"
      aria-labelledby="confirm-dialog-title"
    >
      {({ close }) => (
        <>
          <header className="p-6">
            <Heading
              slot="title"
              className="text-lg text-surface-alt-90 font-semibold"
            >
              {title}
            </Heading>
          </header>
          <section className="p-6 pb-4 text-center text-surface-alt-90 text-base font-medium">
            {children}
          </section>
          <footer className="flex justify-end gap-2.5 px-6 py-4">
            <Button
              ghost
              variant="surface"
              className="justify-center"
              onPress={() => {
                onCancel?.();
                close();
              }}
            >
              {cancelTitle}
            </Button>
            <Button
              variant="danger"
              IconLeft={BinIcon}
              loading={loading}
              className="justify-center"
              {...buttonProps}
              onPress={async () => {
                await onConfirm();
                close();
              }}
            >
              {buttonTitle}
            </Button>
          </footer>
          {image !== null && (
            <img
              src={image}
              alt=""
              aria-hidden="true"
              className="h-26 w-26 absolute -top-4 right-0.5"
            />
          )}
        </>
      )}
    </Dialog>
  );
};

export default ConfirmationDialog;
